import { deleteJson, getJson, pathSegment, postJson, putJson } from "./client";

/**
 * The platform's organizations service: a person's record, their profile
 * as WorkOS holds it, what they agreed to, and the end of their account.
 * The shapes are `platform_organizations_api`, in platform/crates.
 */
export const USERS_BASE = "/v1/users";

/** `platform_organizations_api::users::UserProfile`. */
export interface UserProfile {
    user_id: string;
    email: string;
    /** False after a change, until the person signs in again. */
    email_verified: boolean;
    first_name: string | null;
    last_name: string | null;
    /** When the person first signed up. */
    member_since?: string | null;
}

/** `UpdateUserProfileRequest`: a field left out is left alone. */
export interface UpdateUserProfile {
    first_name?: string;
    last_name?: string;
    email?: string;
}

const userPath = (userId: string) => `${USERS_BASE}/${pathSegment(userId)}`;

/**
 * The person signed in, as organizations knows them. Their record there is
 * made the first time this is asked, and with it their envoy and their
 * Free plan, so it is the first thing asked after sign-in.
 */
export async function getMe(): Promise<UserProfile> {
    return getJson(`${USERS_BASE}/me`);
}

export async function getProfile(userId: string): Promise<UserProfile> {
    return getJson(`${userPath(userId)}/profile`);
}

export async function updateProfile(
    userId: string,
    change: UpdateUserProfile,
): Promise<UserProfile> {
    return putJson(`${userPath(userId)}/profile`, change);
}

/** `ConsentAction`: the control the person used. */
export type ConsentAction =
    "SIGNUP_CHECKBOX" | "REACCEPT_PROMPT" | "CONSENT_BANNER" | "SETTINGS_TOGGLE";

export type TermsStatus = "MISSING" | "BEHIND" | "CURRENT";
export type AgeStatus = "MISSING" | "CONFIRMED";
/** Only `GRANTED` lets analytics run. */
export type AnalyticsStatus = "UNASKED" | "GRANTED" | "BEHIND" | "WITHDRAWN";

/** The versions in force, each a day written `YYYY-MM-DD`. A record must name the same. */
export interface ConsentVersions {
    terms_version: string;
    privacy_version: string;
    analytics_version: string;
}

/** `Consents`: where the person stands, and the newest record of each kind. */
export interface Consents {
    user_id: string;
    required: ConsentVersions;
    terms: {
        status: TermsStatus;
        latest:
            | ({
                  terms_version: string;
                  privacy_version: string;
                  action: ConsentAction;
                  recorded_at: string;
              } & KeptOrigin)
            | null;
    };
    age: {
        status: AgeStatus;
        latest: ({ action: ConsentAction; recorded_at: string } & KeptOrigin) | null;
    };
    analytics: {
        status: AnalyticsStatus;
        latest:
            | ({
                  granted: boolean;
                  version: string;
                  action: ConsentAction;
                  recorded_at: string;
              } & KeptOrigin)
            | null;
    };
}

/** `ConsentSource`: which client, and which part of it, a record came from. */
export type ConsentSource = "WEBAPP" | "EXTENSION_PANEL" | "EXTENSION_POPUP";

/** Where every record came from, which organizations requires on each: this client and its build. */
interface RecordOrigin {
    source: ConsentSource;
    client_version: string;
}

/** Where a kept record came from; `null` on one made before sources were kept. */
interface KeptOrigin {
    source?: ConsentSource | null;
    client_version?: string | null;
}

/** `RecordConsentRequest`: one record, named by `kind`. */
export type ConsentRecord = RecordOrigin &
    (
        | {
              kind: "TERMS";
              terms_version: string;
              privacy_version: string;
              action: "SIGNUP_CHECKBOX" | "REACCEPT_PROMPT";
              /** True records the age acknowledgment too. Left out otherwise: false is refused. */
              age_confirmed?: true;
          }
        | { kind: "AGE"; action: "SIGNUP_CHECKBOX" | "REACCEPT_PROMPT" }
        | {
              kind: "ANALYTICS";
              granted: boolean;
              version: string;
              action: "SIGNUP_CHECKBOX" | "CONSENT_BANNER" | "SETTINGS_TOGGLE";
          }
    );

export async function getConsents(userId: string): Promise<Consents> {
    return getJson(`${userPath(userId)}/consents`);
}

/**
 * Adds one record, timed by the service, and answers where the person
 * now stands. 412 for a version not in force, 429 past 60 an hour.
 */
export async function recordConsent(userId: string, record: ConsentRecord): Promise<Consents> {
    return postJson(`${userPath(userId)}/consents`, record);
}

/** Ends the account at WorkOS, and with it every session. Answers 204. */
export async function deleteAccount(userId: string): Promise<void> {
    return deleteJson(userPath(userId));
}
